'use client';
import { useEffect, useState } from 'react';
import { api } from './api';

export type ModuleKey = 'orders' | 'routes' | 'tachograph' | 'inventory' | 'fleet' | 'marketing' | 'ai' | 'contacts' | 'tour-planner';

const DEFAULTS: Record<ModuleKey, boolean> = {
  orders: true, routes: true, tachograph: true, inventory: true,
  fleet: true, marketing: true, ai: true, contacts: true,
  'tour-planner': false,
};

let cache: Record<ModuleKey, boolean> | null = null;
const subscribers = new Set<(v: Record<ModuleKey, boolean>) => void>();

async function fetchOnce() {
  try {
    const res = await api.get('/modules/enabled');
    cache = { ...DEFAULTS, ...res };
    subscribers.forEach(fn => fn(cache!));
  } catch {
    cache = { ...DEFAULTS };
    subscribers.forEach(fn => fn(cache!));
  }
}

/**
 * Reactive hook for module visibility flags. First call triggers a single
 * fetch and caches the result; subsequent components share the cache.
 * Call `refresh()` after toggling in admin to propagate changes.
 */
export function useModules() {
  const [modules, setModules] = useState<Record<ModuleKey, boolean>>(cache || DEFAULTS);

  useEffect(() => {
    if (cache) {
      setModules(cache);
    } else {
      fetchOnce();
    }
    subscribers.add(setModules);
    return () => { subscribers.delete(setModules); };
  }, []);

  return modules;
}

export async function refreshModules() {
  await fetchOnce();
}
